import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import cn from 'classnames';
import { ContentPageNavigationItem } from '@adapters/prismic/types/contentPage';
import IconWrapper from '@components/ui/Icons';
import styles from './ContentPageNavigation.module.scss';

interface NavigationLevelProps {
  navItems: ContentPageNavigationItem[];
  classNames?: string | string[];
}

const NavigationLevel = ({ navItems, classNames }: NavigationLevelProps) =>
  navItems.length > 0 ? (
    <ul className={cn(styles.navigationLevel, classNames)}>
      {navItems.map((navItem) => (
        <NavigationItem
          title={navItem.title}
          slug={navItem.slug}
          isOpen={navItem.isOpen}
          subNav={navItem.subNav}
          isSelected={navItem.isSelected}
          key={`nav-item-${navItem.slug}`}
        />
      ))}
    </ul>
  ) : null;

const NavigationItem = ({
  title,
  isOpen,
  isSelected,
  slug = '',
  subNav = [],
}: ContentPageNavigationItem) => {
  const { t } = useTranslation('common');

  const [isExpanded, setIsExpanded] = useState(isOpen);
  useEffect(() => {
    if (isOpen) setIsExpanded(isOpen);
  }, [isOpen]);

  if (!title || !slug) return null;

  const toggleIsOpen = () => setIsExpanded(!isExpanded);

  return (
    <li
      className={cn(styles.navItem, {
        [styles.navItemSelected]: isSelected,
      })}
    >
      <div className={styles.navItemButtonsContainer}>
        <Link href={slug}>{title}</Link>

        {subNav.length > 0 && (
          <button
            type="button"
            onClick={toggleIsOpen}
            className={cn(styles.navItemOpenButton, {
              [styles.navItemOpenButtonClosed]: !isExpanded,
            })}
            aria-label={
              isExpanded
                ? t('content.navigation.button.aria.label.close')
                : t('content.navigation.button.aria.label.open')
            }
          >
            <IconWrapper icon="caret-down" width={14} />
          </button>
        )}
      </div>

      <NavigationLevel
        navItems={subNav}
        classNames={cn({ [styles.navigationLevelHidden]: !isExpanded })}
      />
    </li>
  );
};

export default NavigationLevel;
